import { apiQueryWithTokenRegional, apiQueryToUploadDataRegional, apiQueryToDownloadData,apiQueryWithToken } from "../../common/services/apiBaseUrlConfig";
import { ENDPOINT } from "../../common/services/endpoint.config";
import { getAccount, getCurrentUserProfile } from "../../common/services/commonService";
import { decryptSasUri } from "../../common/utility/cryptoUtils";
import logger from "../../common/utils/logger";

const CHATBOT_BASE_URL_US = process.env.REACT_APP_API_URL_US;
const CHATBOT_BASE_URL=process.env.REACT_APP_API_URL;
function chatbotUrl() {
  return `${CHATBOT_BASE_URL_US}${ENDPOINT.CHATBOT_QUERY.replace("baseUrl", "")}`;
}

function trendingUrl(topCount) {
  return `${CHATBOT_BASE_URL_US}${ENDPOINT.CHATBOT_TRENDING_QUERIES.replace("baseUrl", "")}?topCount=${topCount}`;
}

function saveHistoryUrl() {
  return `${CHATBOT_BASE_URL_US}${ENDPOINT.CHATBOT_SAVE_HISTORY.replace("baseUrl", "")}`;
}

function listHistoryUrl(userEmailId) {
  return `${CHATBOT_BASE_URL_US}${ENDPOINT.CHATBOT_LIST_HISTORY.replace("baseUrl", "")}?userEmailId=${encodeURIComponent(userEmailId)}`;
}

function historySasUrl(sessionId) {
  return `${CHATBOT_BASE_URL_US}${ENDPOINT.CHATBOT_HISTORY_SAS_URL.replace("baseUrl", "")}?historySessionId=${encodeURIComponent(sessionId)}`;
}

function viewFileContentUrl(fileName) {
  return `${CHATBOT_BASE_URL_US}${ENDPOINT.CHATBOT_VIEW_FILE_CONTENT.replace("baseUrl", "")}?filename=${encodeURIComponent(fileName)}`;
}

function chatLogUrl() {
  return `${CHATBOT_BASE_URL}${ENDPOINT.CHATBOT_CHAT_LOG.replace("baseUrl", "")}`;
}

function chatFeedbackUrl() {
  return `${CHATBOT_BASE_URL}${ENDPOINT.CHATBOT_CHAT_FEEDBACK.replace("baseUrl", "")}`;
}

// Maps completed conversation turns to the API's { role, content } history format.
export function buildHistory(turns = []) {
  const history = [];
  turns.forEach((turn) => {
    if (turn.userText) {
      history.push({ role: "user", content: turn.userText });
    }
    const variant = turn.variants?.[turn.activeIndex];
    if (variant?.paragraphs?.length) {
      history.push({ role: "assistant", content: variant.paragraphs.join("\n\n") });
    }
  });
  return history;
}

const FILENAME_RE = /\.[a-z0-9]{1,6}$/i;

// Returns a source's display label, accepting both legacy string sources and
// the newer { label, file } object shape (used by restored sessions).
export function sourceLabel(src) {
  if (typeof src === "string") return src;
  return src?.label ?? src?.file ?? "";
}

// Returns the downloadable filename for a source, if any.
export function sourceFile(src) {
  if (typeof src === "string") return FILENAME_RE.test(src) ? src : "";
  return src?.file ?? "";
}

// Normalizes a citation into { label, file } where label is the display string
// ("heading (p.N)") and file is the source document filename when available.
function formatCitation(c) {
  if (typeof c === "string") {
    return c ? { label: c, file: FILENAME_RE.test(c) ? c : "" } : null;
  }
  if (!c || typeof c !== "object") return null;
  const label = c.heading ?? c.name ?? c.title ?? c.document ?? c.source ?? c.source_file;
  if (!label) return null;
  const fileCandidate = c.source_file ?? c.document ?? c.file ?? c.file_name ?? c.fileName ?? c.name;
  const file = typeof fileCandidate === "string" && FILENAME_RE.test(fileCandidate)
    ? fileCandidate
    : (typeof label === "string" && FILENAME_RE.test(label) ? label : "");
  const display = c.page_number != null ? `${label} (p.${c.page_number})` : label;
  return { label: display, file };
}

function extractSources(data) {
  // Preferred API shape: each entry already carries a ready-to-use label
  // (e.g. "Source_1"), shown as-is, and the download filename comes
  // straight from file_name.
  if (Array.isArray(data?.cleaned_sources)) {
    return data.cleaned_sources
      .filter((c) => c && typeof c === "object" && typeof c.file_name === "string" && c.file_name)
      .map((c) => ({ label: c.label ?? c.ref ?? c.doc_id ?? c.file_name, file: c.file_name }));
  }

  // Legacy API shape: each entry carries file/page/deal metadata but no
  // heading/title to derive a label from, so sources are labeled by their
  // position ("Source1", "Source2", ...) and the download filename comes
  // straight from file_name.
  if (Array.isArray(data?.resolved_citations)) {
    return data.resolved_citations
      .filter((c) => c && typeof c === "object" && typeof c.file_name === "string" && c.file_name)
      .map((c, index) => ({ label: `Source${index + 1}`, file: c.file_name }));
  }

  const raw = data?.sources ?? data?.citations ?? data?.documents ?? data?.references;
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const result = [];
  raw.map(formatCitation).forEach((s) => {
    if (!s || seen.has(s.label)) return;
    seen.add(s.label);
    result.push(s);
  });
  return result;
}

// Defensively pulls an answer string out of an unknown response shape and
// splits it into paragraphs, plus any source citations the API returned.
export function parseAnswer(data) {
  let text;
  if (typeof data === "string") {
    text = data;
  } else if (data && typeof data === "object") {
    text = data.answer ?? data.response ?? data.result ?? data.content ?? data.message ?? data.text;
    if (typeof text !== "string") text = "";
  } else {
    text = "";
  }

  const paragraphs = text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);

  return {
    paragraphs: paragraphs.length ? paragraphs : [String(text || "").trim() || "No response was returned."],
    sources: extractSources(data),
  };
}

export async function queryChatbot({ query }) {
  try {
    const { data } = await apiQueryWithTokenRegional.post(chatbotUrl(), { question: query });
    return parseAnswer(data);
  } catch (error) {
    logger.error("Chatbot query failed:", error);
    throw error;
  }
}

function coerceQueryString(item) {
  if (typeof item === "string") return item.trim();
  if (item && typeof item === "object") {
    const val = item.query_text ?? item.queryText ?? item.query ?? item.text ?? item.question ?? item.title ?? item.value ?? item.prompt ?? item.name;
    if (typeof val === "string") return val.trim();
  }
  return "";
}

// Finds the first array of usable query items anywhere in an unknown response
// shape (handles plain arrays, common wrapper keys, and nested objects).
function findQueryArray(node, depth = 0) {
  if (depth > 4 || node == null) return null;
  if (Array.isArray(node)) {
    return node.some((el) => coerceQueryString(el)) ? node : null;
  }
  if (typeof node === "object") {
    const preferredKeys = [
      "trendingQueries", "trending_queries", "queries", "questions",
      "results", "items", "data", "value",
    ];
    for (const key of preferredKeys) {
      if (key in node) {
        const found = findQueryArray(node[key], depth + 1);
        if (found) return found;
      }
    }
    for (const val of Object.values(node)) {
      const found = findQueryArray(val, depth + 1);
      if (found) return found;
    }
  }
  return null;
}

// Defensively pulls a list of trending question strings out of an unknown shape.
export function parseTrendingQueries(data) {
  const raw = findQueryArray(data);
  if (!Array.isArray(raw)) return [];
  return raw.map(coerceQueryString).filter(Boolean);
}

export async function getTrendingQueries(topCount = 4) {
  try {
    const { data } = await apiQueryWithTokenRegional.get(trendingUrl(topCount));
    logger.info("Chatbot trending queries response:", data);
    return parseTrendingQueries(data);
  } catch (error) {
    logger.error("Chatbot trending queries failed:", error);
    throw error;
  }
}

function tryParseJson(text) {
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch {
    return { ok: false };
  }
}

// Line-based SSE parser. The API may separate records with a single newline
// ("event: token\ndata: {...}\nevent: token\n...") instead of a blank line, so
// each complete JSON data line is dispatched immediately; non-JSON data lines
// are buffered until a blank line or the next "event:" line.
function createSseLineParser(dispatch) {
  let event = "message";
  let pending = [];

  const flush = () => {
    if (!pending.length) return;
    const raw = pending.join("\n");
    pending = [];
    const parsed = tryParseJson(raw);
    dispatch(event, parsed.ok ? parsed.value : { text: raw });
  };

  return (rawLine) => {
    const line = rawLine.endsWith("\r") ? rawLine.slice(0, -1) : rawLine;
    if (line === "") {
      flush();
      event = "message";
      return;
    }
    if (line.startsWith(":")) return;
    if (line.startsWith("event:")) {
      flush();
      event = line.slice(6).trim();
      return;
    }
    if (line.startsWith("data:")) {
      const value = line.slice(5).replace(/^ /, "");
      if (!pending.length) {
        const parsed = tryParseJson(value);
        if (parsed.ok) {
          dispatch(event, parsed.value);
          return;
        }
      }
      pending.push(value);
    }
  };
}

const nowMs = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());

// Streams the answer token-by-token via SSE. Calls onToken(text) for each token
// and resolves with the final { paragraphs, sources, text, responseTimeMs, eval }
// once the stream completes. responseTimeMs spans request start to end of
// stream; eval is the API's judge/evaluation object (route, groundedness,
// factual_precision, relevance, completeness, citation_validity, overall,
// judge_reasoning, timestamp, ...), or null when the API didn't return one.
export async function streamChatbot({ query, onToken, signal }) {
  const account = getAccount();
  let accumulated = "";
  let doneData = null;
  const startedAt = nowMs();
  const elapsed = () => Math.round(nowMs() - startedAt);

  try {
    const response = await fetch(chatbotUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        accept: "text/event-stream",
        ...(account?.idToken ? { Authorization: `Bearer ${account.idToken}` } : {}),
      },
      body: JSON.stringify({ question: query }),
      signal,
    });

    if (!response.ok) throw new Error(`Chatbot request failed with status ${response.status}`);
    if (!response.body || !response.body.getReader) {
      const data = await response.json();
      return {
        ...parseAnswer(data),
        text: typeof data === "string" ? data : data?.answer ?? "",
        responseTimeMs: elapsed(),
        eval: (data && typeof data === "object" ? data.eval : null) ?? null,
      };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    const handleEvent = (event, data) => {
      if (event === "done") {
        doneData = data;
      } else if (typeof data?.text === "string") {
        accumulated += data.text;
        if (onToken) onToken(data.text);
      }
    };
    const handleLine = createSseLineParser(handleEvent);

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl;
      while ((nl = buffer.indexOf("\n")) !== -1) {
        handleLine(buffer.slice(0, nl));
        buffer = buffer.slice(nl + 1);
      }
    }
    buffer += decoder.decode();
    if (buffer) handleLine(buffer);
    handleLine("");

    const finalData = doneData ?? { answer: accumulated };
    const finalText = typeof finalData?.answer === "string" ? finalData.answer : accumulated;
    return {
      ...parseAnswer(finalData?.answer != null ? finalData : { ...finalData, answer: accumulated }),
      text: finalText,
      responseTimeMs: elapsed(),
      eval: finalData?.eval ?? null,
    };
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    logger.error("Chatbot stream failed:", error);
    throw error;
  }
}

// ─── Chat history: save / list / load ──────────────────────────────────────────

// Serializes the live conversation into the persisted session shape, dropping
// transient UI-only fields so the saved JSON matches sample-conversation.json.
export function buildSessionJson(sessionId, turns = [], mode = "Deep Research") {
  const now = new Date().toISOString();
  const cleanTurns = turns.map((t) => ({
    id: t.id,
    userText: t.userText,
    time: t.time,
    activeIndex: t.activeIndex ?? 0,
    variants: (t.variants || []).map((v) => ({
      paragraphs: v.paragraphs || [],
      sources: v.sources || [],
      feedback: v.feedback ?? null,
      responseTimeMs: Number.isFinite(v.responseTimeMs) ? v.responseTimeMs : null,
      queryId: v.queryId ?? null,
    })),
  }));
  return {
    conversationId: sessionId,
    title: turns[0]?.userText?.trim() || "New conversation",
    createdOn: now,
    updatedOn: now,
    mode,
    turns: cleanTurns,
    history: buildHistory(turns),
  };
}

// Persists the current session snapshot as a JSON file (multipart). Best-effort:
// never throws to the caller so a failed save can't break the chat UX.
export async function saveChatHistory({ sessionId, turns, mode }) {
  try {
    const account = getAccount();
    const session = buildSessionJson(sessionId, turns, mode);
    const blob = new Blob([JSON.stringify(session, null, 2)], { type: "application/json" });
    const file = new File([blob], `${sessionId}.json`, { type: "application/json" });

    const formData = new FormData();
    formData.append("file", file);
    formData.append("historySessionId", sessionId);
    formData.append("userName", account?.name ?? "");
    formData.append("userEmailId", account?.username ?? "");

    await apiQueryToUploadDataRegional.post(saveHistoryUrl(), formData, {
      // Let the browser set the multipart boundary.
      headers: { "Content-Type": undefined },
    });
    return true;
  } catch (error) {
    logger.error("Chatbot save history failed:", error);
    return false;
  }
}

function coerceSessionTitle(item, fallback) {
  if (item && typeof item === "object") {
    const val = item.title ?? item.name ?? item.firstQuery ?? item.first_query ?? item.query ?? item.conversationTitle;
    if (typeof val === "string" && val.trim()) return val.trim();
  }
  // The list API has no title field; show the session id instead.
  return fallback;
}

// Maps an unknown list response into [{ historySessionId, title, lastQueriedOn }].
export function parseHistorySessions(data) {
  const raw = Array.isArray(data)
    ? data
    : data?.sessions ?? data?.history ?? data?.results ?? data?.items ?? data?.data ?? data?.value ?? [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const id = item.historySessionId ?? item.history_session_id ?? item.sessionId ?? item.session_id ?? item.id;
      if (!id) return null;
      const sessionId = String(id);
      const lastQueriedOn = item.lastQueriedOn ?? item.last_queried_on ?? item.updatedOn ?? item.updated_on ?? item.updated_dated ?? item.createdOn ?? item.created_on ?? item.date_created ?? null;
      return { historySessionId: sessionId, title: coerceSessionTitle(item, sessionId), lastQueriedOn };
    })
    .filter(Boolean);
}

export async function listChatHistory(userEmailId) {
  try {
    const email = userEmailId ?? getAccount()?.username;
    const { data } = await apiQueryWithTokenRegional.get(listHistoryUrl(email));
    logger.info("Chatbot history list response:", data);
    return parseHistorySessions(data);
  } catch (error) {
    logger.error("Chatbot list history failed:", error);
    throw error;
  }
}

export async function getHistorySasUrl(sessionId) {
  const { data } = await apiQueryWithTokenRegional.get(historySasUrl(sessionId));
  const url = typeof data === "string" ? data : data?.sasUrl ?? data?.sas_url ?? data?.url ?? data?.uri;
  if (typeof url !== "string" || !url) throw new Error("No SAS url returned for session");
  return url;
}

// Resolves a previous session's stored JSON by fetching its SAS url blob.
export async function loadChatSession(sessionId) {
  try {
    const sasUrl = await getHistorySasUrl(sessionId);
    const response = await fetch(sasUrl);
    if (!response.ok) throw new Error(`Failed to fetch session file (status ${response.status})`);
    const session = await response.json();
    return session;
  } catch (error) {
    logger.error("Chatbot load session failed:", error);
    throw error;
  }
}

// ─── Source documents ──────────────────────────────────────────────────────────

// Returns a source document's decrypted SAS URL for direct preview. The API
// responds with the same { encryptedSasUri, fileName } shape as
// api/partners/ViewFilesContent.
export async function fetchSourceDocumentUrl(fileName) {
  try {
    const { data } = await apiQueryToDownloadData.post(viewFileContentUrl(fileName), {
      fileName: String(fileName),
    });
    const encryptedSasUri = data?.encryptedSasUri;
    if (!encryptedSasUri) throw new Error("No file URL was returned.");

    const decryptedSasUri = decryptSasUri(encryptedSasUri);
    if (!decryptedSasUri) throw new Error("Unable to decrypt the file URL.");
    return decryptedSasUri;
  } catch (error) {
    logger.error("Chatbot document view failed:", error);
    throw error;
  }
}

// ─── Telemetry: chat/log + chat/feedback ───────────────────────────────────────

// Logs one completed query/response turn (success or failure) and returns the
// query_id the API assigns to it, for later correlation with chat/feedback.
// Best-effort: never throws, so a logging failure can't break the chat UX.
export async function logChatQuery({ query, answer, succeeded, errorMessage, historySessionId }) {
  try {
    const account = getAccount();
    const profile = await getCurrentUserProfile();
    const evalData = (succeeded && answer?.eval) || {};

    const payload = {
      ai_user_prompt: query,
      response_time: answer?.responseTimeMs ?? null,
      results: succeeded ? answer?.text ?? "" : "",
      emailId: account?.username ?? "",
      location: [profile.city, profile.country].filter(Boolean).join(", "),
      succeeded: !!succeeded,
      additional_comments: "",
      error_message: succeeded ? "" : errorMessage ?? "",
      search_type: "AI Chatbot",
      user_designation: profile.jobTitle ?? "",
      history_session_id: historySessionId ?? "",
      route: evalData.route ?? "",
      groundedness: evalData.groundedness ?? null,
      factual_precision: evalData.factual_precision ?? null,
      relevance: evalData.relevance ?? null,
      completeness: evalData.completeness ?? null,
      citation_validity: evalData.citation_validity ?? null,
      overall: evalData.overall ?? null,
      judge_reasoning: evalData.judge_reasoning ?? "",
      timestamp: evalData.timestamp ?? new Date().toISOString(),
    };

    const { data } = await apiQueryWithToken.post(chatLogUrl(), payload);
    return data?.query_id ?? null;
  } catch (error) {
    logger.error("Chatbot chat/log failed:", error);
    return null;
  }
}

// Submits Like/Dislike feedback for a previously logged query. Best-effort:
// never throws, mirroring saveChatHistory's fire-and-forget pattern.
export async function submitChatFeedback({ queryId, historySessionId, sentiment, category, comment }) {
  try {
    const account = getAccount();
    const payload = {
      userEmailId: account?.username ?? "",
      feedback_category: category ?? "",
      feedback: comment ?? "",
      history_session_id: historySessionId ?? "",
      query_id: queryId ?? 1,
      sentiment: sentiment ?? "",
    };

    await apiQueryWithTokenRegional.post(chatFeedbackUrl(), payload);
    return true;
  } catch (error) {
    logger.error("Chatbot chat/feedback failed:", error);
    return false;
  }
}
